import { useState, useEffect } from 'preact/hooks';
import { T } from '../styles/tokens';
import { Button, Divider } from '../components/ui';

const models = [
  { name: 'Claude Opus 4.6', provider: 'Anthropic', direct: 25, router: 12.5 },
  { name: 'Claude Sonnet 4.5', provider: 'Anthropic', direct: 15, router: 7.5 },
  { name: 'Claude Haiku 4.5', provider: 'Anthropic', direct: 4, router: 2 },
  { name: 'GPT-5.4', provider: 'OpenAI', direct: 15, router: 7.5 },
  { name: 'GPT-4.1', provider: 'OpenAI', direct: 8, router: 4 },
];

const faqs = [
  { q: 'Como voces oferecem 50% de desconto?', a: 'Usamos batch processing, prompt caching, smart routing e acordos de volume com provedores. Essas otimizacoes nos permitem repassar economia real.' },
  { q: 'E o mesmo Claude e GPT?', a: 'Sim. Roteamos direto para as APIs da Anthropic e OpenAI. Mesmos modelos, mesma qualidade, mesmas capacidades. Apenas mais barato.' },
  { q: 'Como migro da OpenAI?', a: 'Troque a base_url para yourdomain.com/v1 e use sua API key do Elon Router. Pronto. Seu codigo existente funciona sem alteracao.' },
  { q: 'E sobre rate limits?', a: 'Nossa infraestrutura suporta milhares de requisicoes simultaneas. Rate limits escalam com seu uso — sem caps artificiais.' },
  { q: 'Meus dados estao seguros?', a: 'Nao armazenamos nem logamos seus prompts ou completions. Todo trafego e criptografado end-to-end.' },
  { q: 'Posso usar em producao?', a: 'Com certeza. Servimos trafego de producao para empresas de todos os tamanhos. SLA de 99.9% uptime incluso.' },
];

const features = [
  {
    title: '100+ Modelos',
    desc: 'Claude, GPT, Llama, Mistral, Gemini — tudo em uma unica API.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
  },
  {
    title: 'Compativel com OpenAI',
    desc: 'Drop-in replacement. Troque base_url, mantenha seu codigo.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    title: 'Dashboard de Uso',
    desc: 'Analytics em tempo real. Acompanhe gastos por modelo e projeto.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
      </svg>
    ),
  },
  {
    title: 'Pague Conforme Usa',
    desc: 'Sem minimos, sem contratos. Pague apenas pelo que consumir.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.warning} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
      </svg>
    ),
  },
  {
    title: '99.9% Uptime',
    desc: 'Infraestrutura enterprise-grade com failover automatico.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    title: 'Smart Routing',
    desc: 'Seleciona automaticamente o modelo mais barato que atende seus requisitos.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
      </svg>
    ),
  },
];

const providers = [
  { name: 'Anthropic', desc: 'Claude Opus, Sonnet, Haiku' },
  { name: 'OpenAI', desc: 'GPT-5, GPT-4.1, o-series' },
  { name: 'Google', desc: 'Gemini Pro, Ultra' },
  { name: 'Meta', desc: 'Llama 4' },
  { name: 'Mistral', desc: 'Mistral Large, Medium' },
];

export function LLMLanding() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [tokens, setTokens] = useState(50);
  const [modelIdx, setModelIdx] = useState(0);
  const m = models[modelIdx];
  const direct = tokens * m.direct;
  const router = tokens * m.router;
  const save = direct - router;

  useEffect(() => {
    document.title = 'Elon Router — APIs de LLM com 50% de desconto';
  }, []);

  return (
    <div style={{
      background: T.bg, color: T.text, minHeight: '100vh', overflowX: 'hidden',
      fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
    }}>
      {/* ---- HERO ---- */}
      <section style={{
        minHeight: '90vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        padding: '80px 20px 60px', position: 'relative',
        background: `radial-gradient(ellipse at 30% 20%, ${T.primaryBg} 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, ${T.purpleBgSubtle} 0%, transparent 50%), ${T.bg}`,
      }}>
        <span style={{
          display: 'inline-block', padding: '6px 16px', borderRadius: T.radiusFull,
          border: `1px solid ${T.border}`, color: T.textSecondary, fontSize: T.textSm,
          marginBottom: 24, background: T.bgCard, fontWeight: T.weightMedium,
        }}>
          Sem cartao de credito necessario
        </span>
        <h1 style={{
          fontSize: 'clamp(32px, 6vw, 60px)', fontWeight: 800, color: T.text,
          margin: 0, lineHeight: 1.1, letterSpacing: '-1px', maxWidth: 800,
        }}>
          APIs de LLM pela
          <span style={{ color: T.primary }}> metade do preco</span>
        </h1>
        <p style={{
          fontSize: 'clamp(16px, 2.5vw, 19px)', color: T.textSecondary,
          maxWidth: 600, marginTop: 20, lineHeight: 1.6,
        }}>
          Acesse Claude Opus, GPT-5, Sonnet e 100+ modelos com 50% de desconto. API compativel com OpenAI. Comece em 2 minutos.
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 36, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="/chat" style={{ textDecoration: 'none' }}>
            <Button variant="primary" size="lg">Comecar Gratis — $5 de Credito</Button>
          </a>
          <a href="#pricing" style={{ textDecoration: 'none' }}>
            <Button variant="ghost" size="lg">Ver Precos</Button>
          </a>
        </div>
      </section>

      {/* ---- MODELOS SUPORTADOS ---- */}
      <section style={{ padding: '60px 20px', maxWidth: T.containerLanding, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
            Provedores Suportados
          </h2>
          <p style={{ fontSize: T.textLg, color: T.textSecondary, marginTop: 8 }}>
            Os melhores modelos de IA, todos em uma unica API
          </p>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
          gap: 12,
        }}>
          {providers.map(p => (
            <div key={p.name} style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: T.radiusMd, padding: '20px 16px', textAlign: 'center',
              transition: `border-color ${T.transitionNormal}`,
            }}>
              <div style={{ fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text, marginBottom: 4 }}>{p.name}</div>
              <div style={{ fontSize: T.textSm, color: T.textMuted }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <Divider spacing={0} />

      {/* ---- FEATURES ---- */}
      <section style={{ padding: '80px 20px', maxWidth: T.containerLanding, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
            Feito para Desenvolvedores
          </h2>
          <p style={{ fontSize: T.textLg, color: T.textSecondary, marginTop: 8 }}>
            Tudo que voce precisa para integrar IA no seu produto
          </p>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(300px, 1fr))`,
          gap: 16,
        }}>
          {features.map((f, i) => (
            <div key={i} style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: T.radiusLg, padding: 24,
              transition: `border-color ${T.transitionNormal}`,
            }}>
              <div style={{ marginBottom: 12 }}>{f.icon}</div>
              <h3 style={{ color: T.text, margin: '0 0 6px', fontSize: T.textLg, fontWeight: T.weightSemibold }}>{f.title}</h3>
              <p style={{ color: T.textSecondary, margin: 0, fontSize: T.textMd, lineHeight: 1.5 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <Divider spacing={0} />

      {/* ---- TABELA DE PRECOS ---- */}
      <section id="pricing" style={{ padding: '80px 20px', maxWidth: T.containerLanding, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
            Compare os Precos
          </h2>
          <p style={{ fontSize: T.textLg, color: T.textSecondary, marginTop: 8 }}>
            Por 1M tokens. Mesmos modelos, metade do custo.
          </p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: T.radiusLg, overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['Modelo', 'Provedor', 'Preco Direto', 'Elon Router', 'Economia'].map(h => (
                    <th key={h} style={{
                      padding: '16px 20px', textAlign: 'left', color: T.textMuted,
                      fontSize: T.textSm, fontWeight: T.weightSemibold,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {models.map((m, i) => (
                  <tr key={m.name} style={{ borderBottom: i < models.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                    <td style={{ padding: '14px 20px', color: T.text, fontWeight: T.weightSemibold, fontSize: T.textMd }}>{m.name}</td>
                    <td style={{ padding: '14px 20px', color: T.textMuted, fontSize: T.textSm }}>{m.provider}</td>
                    <td style={{ padding: '14px 20px', color: T.textMuted, textDecoration: 'line-through', fontSize: T.textMd }}>${m.direct.toFixed(2)}</td>
                    <td style={{ padding: '14px 20px', color: T.text, fontWeight: T.weightBold, fontSize: T.textMd }}>${m.router.toFixed(2)}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{
                        background: T.successBg, color: T.success,
                        padding: '4px 10px', borderRadius: T.radiusFull,
                        fontSize: T.textSm, fontWeight: T.weightBold,
                      }}>-50%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p style={{ textAlign: 'center', color: T.textMuted, marginTop: 16, fontSize: T.textSm }}>
          Input tokens tambem com 50% de desconto. Precos completos no dashboard.
        </p>
      </section>

      <Divider spacing={0} />

      {/* ---- COMO FUNCIONA ---- */}
      <section style={{ padding: '80px 20px', maxWidth: T.containerLanding, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
            Comece em 2 Minutos
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 40 }}>
          {[
            { step: '1', title: 'Crie sua Conta', desc: 'Cadastro gratis. Sem cartao.', color: T.primary },
            { step: '2', title: 'Pegue sua API Key', desc: 'Uma chave, todos os modelos.', color: T.purple },
            { step: '3', title: 'Troque Uma Linha', desc: 'Substitua base_url. Pronto.', color: T.success },
          ].map(s => (
            <div key={s.step} style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: T.radiusLg, padding: 24,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: `${s.color}20`,
                color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: T.textMd, fontWeight: T.weightBold, marginBottom: 12,
              }}>{s.step}</div>
              <h3 style={{ color: T.text, margin: '0 0 4px', fontSize: T.textLg, fontWeight: T.weightSemibold }}>{s.title}</h3>
              <p style={{ color: T.textSecondary, margin: 0, fontSize: T.textMd }}>{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Bloco de codigo */}
        <div style={{
          background: T.bgCard, border: `1px solid ${T.border}`,
          borderRadius: T.radiusLg, padding: 24,
          fontFamily: T.fontMono, fontSize: T.textSm, lineHeight: 1.9, overflowX: 'auto',
          maxWidth: 700, margin: '0 auto',
        }}>
          <div><span style={{ color: T.textMuted }}># Antes (preco cheio)</span></div>
          <div><span style={{ color: T.textSecondary }}>client</span> <span style={{ color: T.textMuted }}>=</span> <span style={{ color: T.primary }}>OpenAI</span><span style={{ color: T.textMuted }}>(</span><span style={{ color: T.warning }}>api_key</span><span style={{ color: T.textMuted }}>=</span><span style={{ color: T.primary }}>"sk-..."</span><span style={{ color: T.textMuted }}>)</span></div>
          <div style={{ height: 16 }} />
          <div><span style={{ color: T.textMuted }}># Depois (50% off com Elon Router)</span></div>
          <div><span style={{ color: T.textSecondary }}>client</span> <span style={{ color: T.textMuted }}>=</span> <span style={{ color: T.primary }}>OpenAI</span><span style={{ color: T.textMuted }}>(</span></div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: T.warning }}>api_key</span><span style={{ color: T.textMuted }}>=</span><span style={{ color: T.primary }}>"ek-sua-chave"</span><span style={{ color: T.textMuted }}>,</span></div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: T.warning }}>base_url</span><span style={{ color: T.textMuted }}>=</span><span style={{ color: T.primary }}>"https://yourdomain.com/v1"</span></div>
          <div><span style={{ color: T.textMuted }}>)</span></div>
        </div>
      </section>

      <Divider spacing={0} />

      {/* ---- CALCULADORA ---- */}
      <section style={{ padding: '80px 20px', maxWidth: T.containerLanding, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
            Calcule sua Economia
          </h2>
        </div>
        <div style={{
          maxWidth: 640, margin: '0 auto', background: T.bgCard,
          border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: 32,
        }}>
          <label style={{ color: T.textSecondary, fontSize: T.textSm, display: 'block', marginBottom: 8, fontWeight: T.weightMedium }}>
            Uso mensal: {tokens}M tokens
          </label>
          <input type="range" min={1} max={500} step={1} value={tokens}
            onInput={(e: any) => setTokens(Number(e.target.value))}
            style={{
              width: '100%', height: 4, borderRadius: 2, appearance: 'none',
              background: `linear-gradient(90deg, ${T.primary}, ${T.purple})`,
              outline: 'none', cursor: 'pointer', marginBottom: 24, accentColor: T.purple,
            }}
          />
          <label style={{ color: T.textSecondary, fontSize: T.textSm, display: 'block', marginBottom: 8, fontWeight: T.weightMedium }}>Modelo</label>
          <select value={modelIdx} onChange={(e: any) => setModelIdx(Number(e.target.value))}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: T.radiusMd,
              border: `1px solid ${T.border}`, background: T.bgInput,
              color: T.text, fontSize: T.textMd, marginBottom: 32, outline: 'none',
            }}>
            {models.map((m, i) => <option key={i} value={i}>{m.name}</option>)}
          </select>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div style={{ color: T.textMuted, fontSize: T.textSm, marginBottom: 4 }}>Custo direto</div>
              <div style={{ color: T.text, fontSize: 24, fontWeight: T.weightBold }}>${direct.toLocaleString()}/mes</div>
            </div>
            <div>
              <div style={{ color: T.textMuted, fontSize: T.textSm, marginBottom: 4 }}>Com Elon Router</div>
              <div style={{ color: T.text, fontSize: 24, fontWeight: T.weightBold }}>${router.toLocaleString()}/mes</div>
            </div>
          </div>
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <div style={{ color: T.success, fontSize: 32, fontWeight: 800 }}>Voce economiza ${save.toLocaleString()}/mes</div>
            <div style={{ color: T.textSecondary, fontSize: T.textMd, marginTop: 4 }}>Isso e ${(save * 12).toLocaleString()} por ano</div>
          </div>
        </div>
      </section>

      <Divider spacing={0} />

      {/* ---- FAQ ---- */}
      <section style={{ padding: '80px 20px', maxWidth: T.containerLanding, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
            Perguntas Frequentes
          </h2>
        </div>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          {faqs.map((f, i) => (
            <div key={i} style={{ borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0' }}>
                <span style={{ color: T.text, fontSize: T.textLg, fontWeight: T.weightSemibold }}>{f.q}</span>
                <span style={{
                  color: T.textMuted, fontSize: 20, transition: `transform ${T.transitionNormal}`,
                  transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0)',
                  flexShrink: 0, marginLeft: 12,
                }}>+</span>
              </div>
              <div style={{ maxHeight: openFaq === i ? 200 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
                <p style={{ color: T.textSecondary, fontSize: T.textMd, lineHeight: 1.7, margin: '0 0 18px', paddingRight: 32 }}>{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- CTA FINAL ---- */}
      <section style={{ padding: '60px 20px 80px' }}>
        <div style={{
          maxWidth: 720, margin: '0 auto', textAlign: 'center', padding: '64px 32px',
          borderRadius: T.radiusXl,
          background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`,
          position: 'relative', overflow: 'hidden',
        }}>
          <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 800, color: T.text, margin: 0 }}>
            Pare de Pagar Caro por IA
          </h2>
          <p style={{ color: T.text, fontSize: T.textLg, marginTop: 12 }}>
            Junte-se a mais de 2.500 desenvolvedores economizando 50% em APIs de LLM
          </p>
          <div style={{ marginTop: 32 }}>
            <a href="/chat" style={{
              display: 'inline-block', padding: '16px 40px', background: '#fff', color: T.bg,
              borderRadius: T.radiusLg, fontWeight: T.weightBold, fontSize: T.textLg,
              textDecoration: 'none', transition: `transform ${T.transitionNormal}`,
            }}>Comecar Gratis — $5 de Credito</a>
          </div>
          <p style={{ color: T.text, fontSize: T.textSm, marginTop: 16 }}>Sem cartao necessario. Cancele quando quiser.</p>
        </div>
      </section>

      {/* ---- FOOTER ---- */}
      <footer style={{ borderTop: `1px solid ${T.border}`, padding: '32px 20px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
          {['Elon Router por WhiteLabelClaw', 'Docs da API', 'Status', 'Contato'].map(l => (
            <span key={l} style={{ color: T.textMuted, fontSize: T.textSm }}>{l}</span>
          ))}
        </div>
        <p style={{ color: T.textMuted, fontSize: T.textXs, margin: 0 }}>2026 WhiteLabelClaw. Todos os direitos reservados.</p>
      </footer>

      <style>{`
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: ${T.purple}; cursor: pointer; border: 2px solid #fff; }
        input[type=range]::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; background: ${T.purple}; cursor: pointer; border: 2px solid #fff; }
        select option { background: ${T.bg}; color: ${T.text}; }
      `}</style>
    </div>
  );
}
